import { isDefined } from "@omni/utils/helper.utils";
import { createContext, MouseEvent, ReactNode, use } from "react";

type HeaderButtonContextValue = {
    // HeaderButton props
    text?: string;
    ariaLabel?: string;
    hiddenText?: boolean;
    icon: string;
    link?: string;
    invert: boolean;
    invertMobile: boolean;
    showMenuOnHover?: boolean;
    alerts?: string;

    // HeaderButtonBase props
    hideChevron?: boolean;
    withOverlay: boolean;
    iconPrefix: string;
    menuElement?: ReactNode;

    // Local props
    isActive: boolean;
    isOverlayOpen: boolean;
    onClick: (event: MouseEvent) => void;
    onCloseMenu: () => void;
};

type HeaderButtonContextProviderProps = {
    // HeaderButton props
    text?: string;
    ariaLabel?: string;
    hiddenText?: boolean;
    icon: string;
    link?: string;
    invert: boolean;
    invertMobile: boolean;
    showMenuOnHover?: boolean;
    alerts?: string;

    // HeaderButtonBase props
    hideChevron?: boolean;
    withOverlay?: boolean;
    iconPrefix?: string;
    menuElement?: ReactNode;

    // Local props
    isActive: boolean;
    isOverlayOpen: boolean;
    onClick: (event: MouseEvent) => void;
    onCloseMenu: () => void;
    children: ReactNode;
};

export const HeaderButtonContext = createContext<
    HeaderButtonContextValue | undefined
>(undefined);

export const HeaderButtonContextProvider = ({
    // HeaderButton props
    text,
    ariaLabel,
    hiddenText,
    icon,
    link,
    invert,
    invertMobile,
    showMenuOnHover,
    alerts,

    // HeaderButtonBase props
    hideChevron,
    withOverlay = false,
    iconPrefix = "las",
    menuElement,

    // Local props
    isActive,
    isOverlayOpen,
    onClick,
    onCloseMenu,
    children
}: HeaderButtonContextProviderProps) => {
    return (
        <HeaderButtonContext
            value={{
                text: text,
                ariaLabel: ariaLabel,
                hiddenText: hiddenText,
                invert: invert,
                link: link,
                icon: icon,
                invertMobile: invertMobile,
                alerts: alerts,
                showMenuOnHover: showMenuOnHover,
                hideChevron: hideChevron,
                iconPrefix: iconPrefix,
                menuElement: menuElement,
                withOverlay: withOverlay,
                isActive: isActive,
                isOverlayOpen: isOverlayOpen,
                onClick: onClick,
                onCloseMenu: onCloseMenu
            }}
        >
            {children}
        </HeaderButtonContext>
    );
};

export const useHeaderButtonContext = () => {
    const context = use<HeaderButtonContextValue | undefined>(
        HeaderButtonContext
    );

    if (!isDefined(context)) {
        throw new Error(`${HeaderButtonContext.displayName} is undefined.`);
    }

    return context;
};
